import { useEffect, useState } from 'react';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import consentService from '../../api/services/consent.service';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import { showToast } from '../../store/useToastStore';
import { copyLink } from '../../utils/clipboard';
import { consentFormLinkUrl } from '../../utils/portalLinks';
import { fmtDate } from '../../utils/formatters';

const PAGE_SIZE = 10;
/* one request's worth for the CSV — the export covers every response, not just
   the page on screen */
const EXPORT_LIMIT = 1000;

export default function ConsentPage() {
  const ctx = useContextStore(selectCurrentContext);
  const networkId = ctx?.schoolId ? ctx.id : null;
  const shareUrl = consentFormLinkUrl(networkId);

  /* Rows are parents, pagination is over submissions — a household that filled
     in both parents contributes two rows to one response. */
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [networkId]);

  useEffect(() => {
    if (!networkId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await consentService.list(networkId, { page, limit: PAGE_SIZE });
        if (!cancelled) {
          setRows(result.rows);
          setTotal(result.total);
          setTotalPages(result.totalPages);
        }
      } catch (err) {
        if (!cancelled) showToast(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkId, page]);

  const exportCsv = async () => {
    if (!networkId) return;
    setExporting(true);
    let all;
    try {
      const result = await consentService.list(networkId, { page: 1, limit: EXPORT_LIMIT });
      all = result.rows;
    } catch (err) {
      showToast(err.message);
      setExporting(false);
      return;
    }
    setExporting(false);
    if (!all.length) { showToast('No responses to export yet'); return; }
    const header = ['Parent', 'Email', 'Phone', 'Student', 'Class Group Chat', 'Submitted', 'Account'];
    const body = all.map((r) => [
      r.name,
      r.email || '',
      r.phone || '',
      r.student,
      r.joinGroupChat ? 'Yes' : 'No',
      r.submittedAt ? fmtDate(r.submittedAt) : '',
      r.userId ? 'Account created' : 'No account',
    ]);
    const csv = [header, ...body].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consent-responses-${ctx.id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast(`Exported ${all.length} row${all.length === 1 ? '' : 's'} ✓`);
  };

  return (
    <div className="sec on">
      <SectionHead
        title="Consent Form"
        sub="One standard consent form, same for every class — send it once and each response creates that parent's account automatically."
      />

      <Card
        title="Shareable Link"
        action={<span className="src-chip" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>Standard form · not editable</span>}
      >
        <div className="link-copy-row">
          <span className="url">{shareUrl}</span>
          <a className="btn btn-ghost btn-sm" href={shareUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            Preview
          </a>
          <button className="btn btn-primary btn-sm" onClick={() => copyLink(shareUrl)}>Copy Link</button>
        </div>
        <div className="hint">Text or email this to your class — no account needed to fill it out.</div>
      </Card>

      <Card
        title="Parents Who Consented"
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="src-chip">{total} so far</span>
            <button className="btn btn-ghost btn-sm" disabled={exporting || !total} onClick={exportCsv}>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        }
      >
        <div className="table-scroll">
        {/* table-cards: on a phone each response becomes a labelled block under
            the parent's name — the data-label on every cell is what it reads */}
        <table className="table table-cards">
          <thead>
            <tr>
              <th>Parent</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Student</th>
              <th>Class Group Chat</th>
              <th>Submitted</th>
              <th>Account</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="empty">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Spinner variant="green" /> Loading responses…
                  </span>
                </td>
              </tr>
            )}
            {!loading && (!rows || rows.length === 0) && (
              <tr>
                <td colSpan={7} className="empty">No responses yet.</td>
              </tr>
            )}
            {!loading &&
              (rows || []).map((r, i) => (
                <tr key={`${r.formId}-${r.role}-${i}`}>
                  <td className="c-main">{r.name || '—'}</td>
                  <td data-label="Email">{r.email || '—'}</td>
                  <td data-label="Phone">{r.phone || '—'}</td>
                  <td data-label="Student">{r.student || '—'}</td>
                  <td data-label="Group chat">{r.joinGroupChat ? <span className="src-chip">Yes</span> : '—'}</td>
                  <td data-label="Submitted">{r.submittedAt ? fmtDate(r.submittedAt) : '—'}</td>
                  {/* userId is blank when the form was filled in without one */}
                  <td data-label="Account">
                    {r.userId ? (
                      <span className="src-chip" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
                        ✓ Account created
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        </div>
        {totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid var(--border)',
            }}
          >
            <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              {total} response{total === 1 ? '' : 's'} · Page {page} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost btn-sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
                ← Prev
              </button>
              <button
                className="btn btn-ghost btn-sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
